import { InMemoryCache } from "@apollo/client/cache";
import { ApolloClient, from } from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { createHttpLink } from "@apollo/client/link/http";
import { parse } from "cookie";
import merge from "deepmerge";
import isEqual from "lodash/isEqual";
import { useRef } from "react";

import { ACCESS_TOKEN_KEY } from "@/utils/const";

export const APOLLO_STATE_PROP_NAME = "__APOLLO_STATE__";

// Default to production API if environment variables are not set
const DEFAULT_API_ENDPOINT = "https://api.plush.shop:8080";

const getApiEndpoint = () => {
  return process.env.NEXT_PUBLIC_API_ENDPOINT || DEFAULT_API_ENDPOINT;
};

const uri = getApiEndpoint();

const createCache = () => {
  return new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          search: {
            keyArgs: ["query"],
            merge(existing, incoming, { readField }) {
              const existingRes =
                existing && existing.items ? { ...existing.items.edges } : {};

              const existCuration =
                existing && existing.curation
                  ? { ...existing?.curation }
                  : null;

              if (incoming && incoming.items && incoming.items.edges) {
                incoming.items.edges.forEach((edge) => {
                  existingRes[readField("id", edge.node)] = edge;
                });
              }

              const res = {
                ...incoming,
                curation: incoming?.curation || existCuration,
                items: {
                  ...(incoming.items || {}),
                  edges: existingRes,
                },
              };

              return res;
            },

            read(existing) {
              if (existing) {
                return {
                  ...existing,
                  items: {
                    ...(existing?.items || {}),
                    edges: Object.values(existing?.items?.edges || {}),
                  },
                };
              }
            },
          },
        },
      },
    },
  });
};

let apolloClient;
export let cache = createCache();

const httpLink = createHttpLink({
  uri: `${uri}/query`,
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
    );
  if (networkError) console.log(`[Network error]: ${networkError}`);
});

export function createApolloClient(req) {
  const authLink = setContext((_, { headers }) => {
    let token = "";

    if (req) {
      const cookie = parse(req.headers?.cookie || "");
      token = cookie ? cookie[ACCESS_TOKEN_KEY] : "";
    } else if (typeof document !== "undefined") {
      const cookies = document.cookie.split(";").reduce((acc, curr) => {
        const [key, value] = curr.trim().split("=");
        acc[key] = value;
        return acc;
      }, {});
      token = cookies[ACCESS_TOKEN_KEY] || "";
    }

    return {
      headers: {
        ...headers,
        authorization: token,
      },
    };
  });

  return new ApolloClient({
    ssrMode: typeof window === "undefined",
    link: from([errorLink, authLink.concat(httpLink)]),
    cache,
  });
}

export function createApolloClientWithEmptyCache(req) {
  const authLink = setContext((_, { headers }) => {
    let token = "";

    if (req) {
      const cookie = parse(req.headers?.cookie || "");
      token = cookie ? cookie[ACCESS_TOKEN_KEY] : "";
    } else if (typeof document !== "undefined") {
      const cookies = document.cookie.split(";").reduce((acc, curr) => {
        const [key, value] = curr.trim().split("=");
        acc[key] = value;
        return acc;
      }, {});
      token = cookies[ACCESS_TOKEN_KEY] || "";
    }

    return {
      headers: {
        ...headers,
        authorization: token,
      },
    };
  });

  return new ApolloClient({
    ssrMode: typeof window === "undefined",
    link: from([errorLink, authLink.concat(httpLink)]),
    cache: createCache(),
  });
}

export function initializeApollo(initialState = null, req) {
  const _apolloClient = apolloClient ?? createApolloClient(req);
  // If your page has Next.js data fetching methods that use Apollo Client,
  //  the initial state gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract();

    // Merge the initialState from getStaticProps/getServerSideProps
    // in the existing cache
    const data = merge(existingCache, initialState, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) =>
          sourceArray.every((s) => !isEqual(d, s))
        ),
      ],
    });
    // Restore the cache with the merged data
    _apolloClient.cache.restore(data);
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === "undefined") return _apolloClient;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;
  return _apolloClient;
}

export function addApolloState(client, pageProps) {
  if (pageProps?.props) {
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }
  return pageProps;
}

export function useApollo(pageProps) {
  const state = pageProps[APOLLO_STATE_PROP_NAME];
  const storeRef = useRef();
  if (!storeRef.current) {
    storeRef.current = initializeApollo(state);
  }
  return storeRef.current;
}
